import React, { useState } from 'react';
import { TicketPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../../components/card';
import { Button } from '../../../components/button';
import FormHeader from '../../../components/form/FormHeader';
import './CreateEGiftCardPage.css';
import {
    E_GIFT_CARD_FORM_ID,
    EGiftCardForm,
} from '../components/form/EGiftCardForm';
import { useCreateEGiftCard } from '../hooks/useCreateEGiftCard';
import type { CreateEGiftCardRequest, EGiftCardFormData } from '../types/eGiftCardForm.types';

const CreateEGiftCardPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { createEGiftCard, isLoading: isCreating } = useCreateEGiftCard();
    const [isSubmissionBlocked, setIsSubmissionBlocked] = useState(false);

    const onSubmit = async (data: EGiftCardFormData) => {
        const giftCards = (data.giftCardItems || []).map((card) => ({
            ...card,
            is_existing: false,
        }));

        const request: CreateEGiftCardRequest = {
            merchant_brand_id: data.merchantBrandId,
            gift_cards: giftCards,
            gift_card_ids: data.giftCardIds,
            valid_from: data.validFrom,
            valid_to: data.validTo,
            timezone: data.timezone,
            is_alert_low_stock: data.isAlertLowStock,
        };

        await createEGiftCard(request);
    };

    return (
        <Card className="border-none rounded-none h-full flex flex-col bg-[#F9FAFB] dark:bg-muted/50">
            <FormHeader
                onBack={() => navigate('/e-gift-card')}
                title={t('createEGiftCard.title')}
                subtitle=""
                backLabel={t('common.back') || 'Back'}
                actions={(
                    <Button
                        className="rounded-lg px-6 py-2 text-sm font-semibold bg-[#00C49A] hover:bg-[#00C49A]/90 text-white gap-2"
                        type="submit"
                        form={E_GIFT_CARD_FORM_ID}
                        disabled={isCreating || isSubmissionBlocked}
                    >
                        <TicketPlus className="h-4 w-4" aria-hidden="true" />
                        {isCreating
                            ? t('createEGiftCard.creating') || 'Creating...'
                            : t('createEGiftCard.createEGiftCard') || 'Create E-Gift Card'}
                    </Button>
                )}
            />

            <div className="p-6">
                <EGiftCardForm
                    onSubmit={onSubmit}
                    onSubmissionBlockedChange={setIsSubmissionBlocked}
                />
            </div>
        </Card>
    );
};

export default CreateEGiftCardPage;
